import * as React from "react";
import {
    List,
    Datagrid,
    Edit,
    Create,
    SimpleForm,
    EditButton,
    TextInput,
    DateField,
    FunctionField,
    useTranslate,
    useRecordContext,
} from 'react-admin';

import EmptyList from "../components/misc/EmptyList";
import HeroSpecialEvents from '../components/form/HeroSpecialEvents';
import { HeroLegacyMigrator } from '../components/form/HeroLegacyMigrator';
import { sanitizeHeroPayload } from '../utils/heroRecordUtils';
import { EditableImage } from '../components/form/EditableImageField';

const entity = "heros";

/** Web hero section: 1920 canvas × (60+384+60) minHeight — tasarim olcusu. */
const HOME_HERO_BANNER_WIDTH = 1920;
const HOME_HERO_BANNER_HEIGHT = 504;
const HOME_HERO_BANNER_ASPECT = HOME_HERO_BANNER_WIDTH / HOME_HERO_BANNER_HEIGHT;

export const HeroList = (props) => {
    const translate = useTranslate();
    const pageTitle = translate('heros.title', { _: 'Ana Sayfa Özel Etkinlik Görseli (Hero)' });

    return (
        <List
            {...props}
            title={pageTitle}
            empty={
                <EmptyList
                    entity={entity}
                    customMessage={translate('heros.empty_msg', { _: 'Hero kaydı yok. "Oluştur" ile ID: home olarak oluşturun.' })}
                />
            }
            hasCreate
            exporter={false}
        >
            <Datagrid>
                <FunctionField
                    label={translate('heros.record', { _: 'Kayıt' })}
                    render={(record) =>
                        record.id === 'home' ? pageTitle : record.id
                    }
                />
                <DateField source="lastupdate" label={translate('heros.last_updated', { _: 'Son Güncelleme' })} />
                <EditButton />
            </Datagrid>
        </List>
    );
};

const heroSaveTransform = (data, recordId = 'home') =>
    sanitizeHeroPayload(data, recordId);

const HeroFormFields = ({ isCreate = false }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    return (
        <React.Fragment>
            {!isCreate ? <HeroLegacyMigrator /> : null}
            {isCreate ? (
                <TextInput
                    source="id"
                    label={"ID"}
                    helperText="Ana sayfa için id: home kullanın"
                />
            ) : null}
            <EditableImage
                source="banner"
                record={record}
                aspect={HOME_HERO_BANNER_ASPECT}
                width={480}
                height={Math.round(480 / HOME_HERO_BANNER_ASPECT)}
                label={translate('heros.bg_image', { _: 'Hero Arka Plan Görseli' })}
                hint={translate('heros.banner_crop_hint', {
                    _: `Home hero ölçüsü ${HOME_HERO_BANNER_WIDTH}×${HOME_HERO_BANNER_HEIGHT}. Kaydetmeden önce kırpın.`,
                })}
                cropTitle={translate('heros.banner_crop_title', {
                    _: `Görseli Kırp (${HOME_HERO_BANNER_WIDTH}×${HOME_HERO_BANNER_HEIGHT})`,
                })}
                cropDesc={translate('heros.banner_crop_desc', {
                    _: 'Kırpma alanı home hero oranında kilitli. Alanı sürükleyip boyutunu ayarlayabilirsiniz.',
                })}
            />
            <HeroSpecialEvents />
        </React.Fragment>
    );
};

export const HeroEdit = (props) => {
    const translate = useTranslate();
    return (
        <Edit
            title={translate('heros.title', { _: 'Ana Sayfa Özel Etkinlik Görseli (Hero)' })}
            {...props}
            transform={(data) => heroSaveTransform(data, props.id ?? 'home')}
            mutationMode="pessimistic"
        >
            <SimpleForm redirect={`/${entity}`}>
                <HeroFormFields />
            </SimpleForm>
        </Edit>
    );
};

export const HeroCreate = (props) => {
    const translate = useTranslate();
    return (
        <Create
            title={translate('heros.title', { _: 'Ana Sayfa Özel Etkinlik Görseli (Hero)' })}
            {...props}
            transform={(data) => heroSaveTransform(data, data?.id ?? 'home')}
        >
            <SimpleForm redirect={`/${entity}`}>
                <HeroFormFields isCreate />
            </SimpleForm>
        </Create>
    );
};
